/** Chart-column → table filter chips (Annual Returns / VAT section tables). */

export type ChartSupplierFilter = {
  sectionKey: string;
  supplier: string;
} | null;

export type ChartCategoryFilter = {
  sectionKey: string;
  category: string;
} | null;

export const chartSupplierName = (supplier: string | null | undefined): string =>
  String(supplier || '').trim() || '—';

export const chartCategoryName = (category: string | null | undefined): string =>
  String(category || '').trim() || '—';

export const toggleChartSupplierFilter = (
  current: ChartSupplierFilter,
  sectionKey: string,
  supplier: string | null | undefined
): ChartSupplierFilter => {
  const normalizedSection = String(sectionKey || '').trim();
  const raw = String(supplier ?? '').trim();
  if (!normalizedSection || !raw) return current;
  const name = chartSupplierName(raw);
  if (current?.sectionKey === normalizedSection && current.supplier === name) return null;
  return { sectionKey: normalizedSection, supplier: name };
};

export const toggleChartCategoryFilter = (
  current: ChartCategoryFilter,
  sectionKey: string,
  category: string | null | undefined
): ChartCategoryFilter => {
  const normalizedSection = String(sectionKey || '').trim();
  const raw = String(category ?? '').trim();
  if (!normalizedSection || !raw) return current;
  const name = chartCategoryName(raw);
  if (current?.sectionKey === normalizedSection && current.category === name) return null;
  return { sectionKey: normalizedSection, category: name };
};

export const rowMatchesChartSupplier = (
  rowSupplier: string | null | undefined,
  filter: ChartSupplierFilter,
  sectionKey: string
): boolean => {
  if (!filter || filter.sectionKey !== sectionKey) return true;
  return chartSupplierName(rowSupplier) === filter.supplier;
};

export const filterRowsByChartSupplier = <T extends { supplier?: string | null }>(
  rows: T[],
  filter: ChartSupplierFilter,
  sectionKey: string
): T[] => {
  if (!filter || filter.sectionKey !== sectionKey) return rows;
  return rows.filter((row) => rowMatchesChartSupplier(row.supplier, filter, sectionKey));
};

export const rowMatchesChartCategory = (
  rowCategory: string | null | undefined,
  filter: ChartCategoryFilter,
  sectionKey: string
): boolean => {
  if (!filter || filter.sectionKey !== sectionKey) return true;
  return chartCategoryName(rowCategory) === filter.category;
};

export const filterRowsByChartCategory = <T extends { category?: string | null }>(
  rows: T[],
  filter: ChartCategoryFilter,
  sectionKey: string,
  categoryForRow: (row: T) => string
): T[] => {
  if (!filter || filter.sectionKey !== sectionKey) return rows;
  return rows.filter((row) => rowMatchesChartCategory(categoryForRow(row), filter, sectionKey));
};

export const filterRowsByChartSupplierAndCategory = <
  T extends { supplier?: string | null; category?: string | null },
>(
  rows: T[],
  supplierFilter: ChartSupplierFilter,
  categoryFilter: ChartCategoryFilter,
  sectionKey: string,
  categoryForRow: (row: T) => string
): T[] => {
  let out = filterRowsByChartSupplier(rows, supplierFilter, sectionKey);
  out = filterRowsByChartCategory(out, categoryFilter, sectionKey, categoryForRow);
  return out;
};

/** Resolve the clicked column index from Recharts 3 chart click state (index may be a string). */
export const chartClickRowIndex = (
  state: { activeTooltipIndex?: number | string | null; activeIndex?: number | string | null }
): number | undefined => {
  const raw = state.activeTooltipIndex ?? state.activeIndex;
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw === 'string' && raw.trim() !== '') {
    const parsed = Number(raw);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
};

export const chartColumnLabelFromBarClick = (
  rows: ReadonlyArray<{ label: string }>,
  data: { payload?: { label?: string | number; supplier?: string | number }; label?: string | number } | undefined,
  index: number | undefined,
  activeLabel?: string | null | undefined
): string => {
  const fromPayload = String(data?.payload?.label ?? data?.payload?.supplier ?? '').trim();
  if (fromPayload) return fromPayload;
  const fromData = String(data?.label ?? '').trim();
  if (fromData) return fromData;
  if (typeof index === 'number' && Number.isFinite(index) && rows[index]?.label) {
    return String(rows[index].label).trim();
  }
  const fromActive = String(activeLabel ?? '').trim();
  if (fromActive) return fromActive;
  return '';
};
